import { toast } from 'sonner';

export const ALLOWED_FILE_TYPES = ['application/pdf', 'image/jpeg', 'image/png', 'image/gif', 'image/webp'];
export const MAX_FILE_SIZE = 20 * 1024 * 1024; // 20MB

export interface FileValidationResult {
  isValid: boolean;
  error?: string;
}

/**
 * Validates a file for upload
 * @param file - The file to validate
 * @param showToast - Whether to show toast notifications for errors
 * @returns Validation result
 */
export const validateFile = (file: File, showToast = true): FileValidationResult => {
  if (!ALLOWED_FILE_TYPES.includes(file.type) && !file.type.startsWith('image/')) {
    const error = 'Invalid file type. Please upload a PDF or image file';
    if (showToast) {
      toast.error('Invalid file type', { description: error });
    }
    return { isValid: false, error };
  }

  if (file.size > MAX_FILE_SIZE) {
    const error = 'File size must be less than 20MB';
    if (showToast) {
      toast.error('File too large', { description: error });
    }
    return { isValid: false, error };
  }

  return { isValid: true };
};

/**
 * Validates multiple files
 * @param files - Array of files to validate
 * @param showToast - Whether to show toast notifications
 * @returns Array of validation results
 */
export const validateFiles = (files: File[], showToast = true): FileValidationResult[] => {
  return files.map((file) => validateFile(file, showToast));
};

/**
 * Generates a unique temporary ID for local attachments
 */
export const generateTempId = (): string => {
  return `local-${Date.now()}-${Math.random().toString(36).substring(2, 11)}`;
};
